<template>
  <v-card class="addon-form-card" elevation="0">
    <v-card-title class="primary white--text addon-form-header">
      <div class="header-copy"> 
        <div class="header-title">
          {{ edit ? "Edit Add-on Programme" : "Add Add-on Programme" }}
        </div>
      </div>

      <v-spacer></v-spacer>

      <v-btn icon small class="close-btn" @click="$emit('closeDialog')">
        <v-icon color="white" small>mdi-close</v-icon>
      </v-btn>
    </v-card-title>

    <v-card-text class="addon-form-body">
      <v-form ref="form" v-model="valid" class="mt-4">
        <v-row dense>
          <v-col cols="12" md="7">
            <div class="field-wrapper">
              <label class="custom-label">
                Name <span class="red--text">*</span>
              </label>
              <v-text-field
                v-model="form.name"
                outlined
                dense
                :rules="[rules.required]"
                hide-details="auto"
                class="mb-4"
              />
            </div>

            <div class="field-wrapper">
              <label class="custom-label">
                Description <span class="red--text">*</span>
              </label>

              <div
                class="editor-wrapper"
                :class="{ 'editor-error': descriptionError }"
              >
                <vue-editor
                  v-model="form.description"
                  :editor-toolbar="simpleToolbar"
                  use-custom-toolbar
                />
              </div>

              <div v-if="descriptionError" class="custom-error">
                Description is required
              </div>
            </div>
          </v-col>

          <v-col cols="12" md="5">
            <v-sheet outlined class="form-panel mt-3">
              <div class="section-title">
                <v-icon small color="primary">mdi-tune-variant</v-icon>
                Configuration
              </div>

              <div class="field-wrapper mb-4">
                <label class="custom-label">
                  Type <span class="red--text">*</span>
                </label>

                <div class="choice-grid">
                  <button
                    v-for="type in types"
                    :key="type"
                    type="button"
                    class="choice-card"
                    :class="{ active: form.type === type }"
                    @click="setType(type)"
                  >
                    <v-icon small>
                      {{
                        type === "Single"
                          ? "mdi-bookmark-outline"
                          : "mdi-package-variant-closed"
                      }}
                    </v-icon>
                    <span>{{ type }}</span>
                  </button>
                </div>

                <div v-if="typeError" class="custom-error">
                  Type is required
                </div>
              </div>

              <div v-if="form.type === 'Combo'" class="field-wrapper mb-4">
                <label class="custom-label">
                  Combo Add-ons <span class="red--text">*</span>
                </label>
                <v-select
                  v-model="form.combo_addons"
                  :items="programAddons"
                  item-text="name"
                  item-value="id"
                  :rules="[rules.arrayMinOne]"
                  outlined
                  hide-details="auto"
                  dense
                  multiple
                  chips
                  clearable
                >
                  <template v-slot:selection="{ item, index }">
                    <v-chip v-if="index < 2" small color="primary" label>
                      {{ item.name }}
                    </v-chip>
                    <span v-if="index === 2" class="grey--text caption">
                      +{{ form.combo_addons.length - 2 }} more
                    </span>
                  </template>
                </v-select>

                <div class="info-text mt-2">
                  <v-icon small color="info">mdi-information-outline</v-icon>
                  Select multiple add-ons to create a combo package
                </div>
              </div>
            </v-sheet>

            <v-sheet outlined class="form-panel mt-4">
              <div class="section-title">
                <v-icon small color="primary">mdi-receipt-text-outline</v-icon>
                GST Details
              </div>

              <div class="field-wrapper">
                <label class="custom-label">
                  GST Included? <span class="red--text">*</span>
                </label>

                <div class="radio-group-container">
                  <div class="radio-group">
                    <label class="radio-option">
                      <input
                        type="radio"
                        :value="true"
                        v-model="form.is_gst_included"
                        @change="setGstIncluded(true)"
                      />
                      <span>Yes</span>
                    </label>
                    <label class="radio-option">
                      <input
                        type="radio"
                        :value="false"
                        v-model="form.is_gst_included"
                        @change="setGstIncluded(false)"
                      />
                      <span>No</span>
                    </label>
                  </div>
                </div>

                <div v-if="gstError" class="custom-error">
                  GST selection is required
                </div>

                <div v-if="form.is_gst_included === true" class="mt-4">
                  <label class="custom-label">
                    GST Type <span class="red--text">*</span>
                  </label>

                  <div class="radio-group">
                    <label class="radio-option">
                      <input
                        type="radio"
                        value="inclusive"
                        v-model="form.gst_type"
                        @change="setGstType('inclusive')"
                      />
                      <span>Inclusive</span>
                    </label>
                    <label class="radio-option">
                      <input
                        type="radio"
                        value="exclusive"
                        v-model="form.gst_type"
                        @change="setGstType('exclusive')"
                      />
                      <span>Exclusive</span>
                    </label>
                  </div>
                </div>
              </div>
            </v-sheet>
          </v-col>
        </v-row>
      </v-form>
    </v-card-text>

    <v-card-actions class="addon-form-actions">
      <v-spacer></v-spacer>

      <v-btn text class="mr-2" @click="closeDialog"> Cancel </v-btn>

      <v-btn 
        color="primary" 
        depressed 
        @click="save"
        :loading="loading"
      >
        {{ edit ? "Update" : "Save" }}
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
import { VueEditor } from "vue2-editor";
import { rules } from "@/helpers/rules";

export default {
  name: "AddonForm",
  
  components: {
    VueEditor,
  },

  props: {
    programAddon: {
      type: Object,
      default: () => ({}),
    },

    edit: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      rules,
      valid: true,
      descriptionError: false,
      typeError: false,
      percentageError: false,
      gstError: false,
      loading: false,
      programAddons: [],

      form: {
        id: undefined,
        name: "",
        description: "",
        type: "Single",
        combo_addons: [],
        is_gst_included: false,
        gst_type: "inclusive",
        gst_percentage: null,
      },

      types: ["Single", "Combo"],

      simpleToolbar: [
        ["bold", "italic", "underline", "strike", "blockquote", "code-block"],
        [{ header: [1, 2, 3, 4, 5, 6, false] }],
        [{ size: ["small", "normal", "large", "huge"] }],
        [{ list: "ordered" }, { list: "bullet" }],
        [{ align: [] }],
        [{ script: "sub" }, { script: "super" }],
        [{ color: [] }, { background: [] }],
        ["clean"],
      ],
    };
  },

  watch: {
    programAddon: {
      handler(val) {
        if (val && Object.keys(val).length > 0) {
          this.form = {
            id: val.id,
            name: val.name || "",
            description: val.description || "",
            type: val.type || "Single",
            combo_addons: val.combo_addons || [],
            is_gst_included: val.is_gst_included ?? false,
            gst_type: val.gst_type || "inclusive",
            gst_percentage: val.gst_percentage ?? (val.is_gst_included ? 18 : null),
          };
        } else {
          this.resetForm();
        }
      },
      immediate: true,
      deep: true,
    },

    "form.description"() {
      this.descriptionError = false;
    },

    "form.type"() {
      this.typeError = false;
    },

    "form.is_gst_included"() {
      this.gstError = false;
    },
  },

  created() {
    this.fetchProgramAddons();
  },

  methods: {
    resetForm() {
      this.form = {
        id: undefined,
        name: "",
        description: "",
        type: "Single",
        combo_addons: [],
        is_gst_included: false,
        gst_type: "inclusive",
        gst_percentage: null,
      };
      this.descriptionError = false;
      this.typeError = false;
      this.gstError = false;
      this.percentageError = false;
    },

    async fetchProgramAddons() {
      try {
        const res = await this.$axios.get("program_addons");
        let addons = res.data.data || [];
        
        // Exclude current addon if editing
        if (this.edit && this.form.id) {
          addons = addons.filter((a) => a.id !== this.form.id);
        }
        
        // Only show Single type add-ons for combo selection
        this.programAddons = addons.filter((a) => a.type === "Single");
        
      } catch (error) {
        console.error("Error fetching program addons:", error);
        this.$store.commit("showSnackbar", {
          message: "Failed to fetch add-ons",
          color: "error",
        });
      }
    },

    validateDescription() {
      const value = this.form.description?.replace(/<(.|\n)*?>/g, "").trim();
      if (!value) {
        this.descriptionError = true;
        return false;
      }
      this.descriptionError = false;
      return true;
    },

    validateType() {
      if (!this.form.type) {
        this.typeError = true;
        return false;
      }
      this.typeError = false;
      return true;
    },

    validatePercentage(value) {
      if (!value && value !== 0) return false;
      const percentage = parseFloat(value);
      if (isNaN(percentage) || percentage < 0 || percentage > 100) {
        this.percentageError = true;
        return false;
      }
      this.percentageError = false;
      return true;
    },

    validateGst() {
      if (this.form.is_gst_included === null || this.form.is_gst_included === undefined) {
        this.gstError = true;
        return false;
      }
      this.gstError = false;

      if (this.form.is_gst_included === true) {
        if (!this.form.gst_percentage && this.form.gst_percentage !== 0) {
          this.percentageError = true;
          return false;
        }
        if (!this.form.gst_type) {
          this.$store.commit("showSnackbar", {
            message: "Please select GST type",
            color: "error",
          });
          return false;
        }
        return this.validatePercentage(this.form.gst_percentage);
      }

      this.percentageError = false;
      return true;
    },

    validateComboAddons() {
      if (this.form.type === "Combo" && 
          (!this.form.combo_addons || this.form.combo_addons.length === 0)) {
        return false;
      }
      return true;
    },

    setType(type) {
      this.form.type = type;
      if (type === "Single") {
        this.form.combo_addons = [];
      }
    },

    setGstIncluded(value) {
      this.form.is_gst_included = value;
      if (value === false) {
        this.form.gst_percentage = null;
        this.form.gst_type = "inclusive";
      } else if (value === true && !this.form.gst_percentage) {
        this.form.gst_percentage = 18; // Default GST percentage
        this.form.gst_type = this.form.gst_type || "inclusive";
      }
      this.gstError = false;
      this.percentageError = false;
    },

    setGstType(type) {
      this.form.gst_type = type;
    },

    prepareForSubmit(formData) {
      const payload = {
        name: formData.name.trim(),
        description: formData.description,
        type: formData.type,
        is_gst_included: formData.is_gst_included,
        active: true,
      };

      // Handle combo_addons
      if (formData.type === "Combo") {
        payload.combo_addons = (formData.combo_addons && formData.combo_addons.length > 0) 
          ? formData.combo_addons 
          : null;
      } else {
        payload.combo_addons = null;
      }

      // Handle GST details
      if (formData.is_gst_included === true) {
        payload.gst_percentage = parseFloat(formData.gst_percentage);
        payload.gst_type = formData.gst_type || "inclusive";
      } else {
        payload.gst_percentage = null;
        payload.gst_type = null;
      }

      // Include ID for edit
      if (formData.id) {
        payload.id = formData.id;
      }

      return payload;
    },

    async save() {
      // Validate form
      const isFormValid = this.$refs.form.validate();
      const isDescriptionValid = this.validateDescription();
      const isTypeValid = this.validateType();
      const isGstValid = this.validateGst();
      const isComboValid = this.validateComboAddons();

      if (!isFormValid || !isDescriptionValid || !isTypeValid || !isGstValid) {
        return;
      }

      if (this.form.type === "Combo" && !isComboValid) {
        this.$store.commit("showSnackbar", {
          message: "Please select at least one add-on for Combo type",
          color: "error",
        });
        return;
      }

      this.loading = true;

      try {
        const payload = this.prepareForSubmit(this.form);
        
        // Emit event for parent component to handle API call
        this.$emit(this.edit ? "update" : "create", payload);
        
        // Close dialog after successful save (parent should handle the API)
        this.closeDialog();
        
      } catch (error) {
        console.error("Error saving add-on:", error);
        this.$store.commit("showSnackbar", {
          message: error.response?.data?.message || "Failed to save add-on",
          color: "error",
        });
      } finally {
        this.loading = false;
      }
    },

    closeDialog() {
      this.resetForm();
      this.$emit('closeDialog');
    },
  },
};
</script>

<style scoped>
.addon-form-card {
  border-radius: 8px;
  overflow: hidden;
}

.addon-form-header {
  border-bottom: 1px solid #e5e7eb;
  padding: 18px 20px;
  background-color: var(--v-primary-base) !important;
}

.header-copy {
  min-width: 0;
}

.header-title {
  font-size: 17px;
  line-height: 1.3;
  font-weight: 500;
}

.close-btn {
  color: white !important;
}

.addon-form-body {
  background: #ffffff;
  padding: 20px;
}

.addon-form-actions {
  background: #ffffff;
  border-top: 1px solid #e5e7eb;
  padding: 14px 20px;
}

.section-title {
  align-items: center;
  color: #111827;
  display: flex;
  font-size: 13px;
  font-weight: 600;
  gap: 8px;
  margin-bottom: 14px;
}

.form-panel {
  border-color: #e5e7eb !important;
  border-radius: 8px;
  padding: 16px;
}

.custom-label {
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 6px;
}

.custom-error {
  color: #ff5252;
  font-size: 12px;
  margin-top: 6px;
}

.field-wrapper {
  width: 100%;
}

.choice-grid {
  display: grid;
  gap: 10px;
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.choice-card {
  align-items: center;
  background: #ffffff;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  color: #374151;
  cursor: pointer;
  display: flex;
  font-size: 13px;
  font-weight: 600;
  gap: 8px;
  justify-content: center;
  min-height: 42px;
  padding: 0 10px;
  transition: border-color 0.15s ease, background 0.15s ease, color 0.15s ease;
  width: 100%;
}

.choice-card:hover {
  border-color: var(--v-primary-base);
  background: #f8fafc;
}

.choice-card.active {
  background: #eef4ff;
  border-color: var(--v-primary-base);
  color: var(--v-primary-base);
}

/* Radio Group Styles */
.radio-group-container {
  margin-top: 4px;
}

.radio-group {
  display: flex;
  gap: 1.5rem;
  flex-wrap: wrap;
}

.radio-option {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  user-select: none;
}

.radio-option input[type="radio"] {
  cursor: pointer;
  margin: 0;
  width: 16px;
  height: 16px;
}

.radio-option span {
  cursor: pointer;
  font-size: 14px;
  color: #374151;
}

.radio-option:hover span {
  color: var(--v-primary-base);
}

.segmented-control {
  background: #f3f4f6;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  display: grid;
  gap: 4px;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  padding: 4px;
}

.segmented-control button {
  background: transparent;
  border: 0;
  border-radius: 6px;
  color: #4b5563;
  cursor: pointer;
  font-size: 13px;
  font-weight: 600;
  min-height: 34px;
  transition: all 0.15s ease;
}

.segmented-control button:hover {
  background: rgba(0, 0, 0, 0.05);
}

.segmented-control button.active {
  background: #ffffff;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.14);
  color: var(--v-primary-base);
}

.editor-wrapper {
  border: 1px solid #d1d5db;
  border-radius: 8px;
  overflow: hidden;
  transition: border-color 0.15s ease;
}

.editor-wrapper:focus-within {
  border-color: var(--v-primary-base);
}

.editor-error {
  border-color: #ff5252 !important;
}

.info-text {
  font-size: 12px;
  color: #6b7280;
  display: flex;
  align-items: center;
  gap: 6px;
}

/* Editor Styles */
::v-deep .ql-toolbar {
  background: #f9fafb;
  border: none;
  border-bottom: 1px solid #e5e7eb;
}

::v-deep .ql-container {
  border: none;
  font-family: inherit;
}

::v-deep .ql-editor {
  min-height: 180px;
  font-size: 14px;
  line-height: 1.6;
}

::v-deep .ql-editor:focus {
  outline: none;
}

/* Vuetify overrides */
::v-deep .v-input--is-focused .v-input__slot {
  border-color: var(--v-primary-base) !important;
}

::v-deep .v-text-field--outlined .v-input__slot {
  border-color: #d1d5db;
}

::v-deep .v-text-field--outlined.v-input--is-focused .v-input__slot {
  border-color: var(--v-primary-base);
  border-width: 1px;
}

/* Number input styling */
::v-deep input[type="number"]::-webkit-inner-spin-button,
::v-deep input[type="number"]::-webkit-outer-spin-button {
  opacity: 0.5;
}
</style>